import React, { useState, useEffect, useRef } from 'react'; 
import BuildPanel from './BuildPanel';
import ProjectStatusPanel from './ProjectStatusPanel';
import { API_BASE } from '../utils/apiBase';

// The amenity/service parameters no geometry can produce — counts and yes/no.
// Grouped to match the compliance card's categories.
const AMENITY_FIELDS = [
  { group: 'Parking', items: [
    { id: 'parking_car', label: 'Car parking (nos.)', type: 'number' },
    { id: 'parking_visitor', label: 'Visitor parking (nos.)', type: 'number' },
    { id: 'parking_two_wheeler', label: 'Two-wheeler (nos.)', type: 'number' },
    { id: 'parking_loading', label: 'Loading bays (nos.)', type: 'number' },
    { id: 'visitor_parking_ground', label: 'Visitor parking at ground (nos.)', type: 'number' },
  ]},
  { group: 'Life safety', items: [
    { id: 'staircase_count', label: 'Staircases (common + fire)', type: 'number' },
    { id: 'lift_count', label: 'Lifts (incl. fire lift)', type: 'number' },
    { id: 'fire_tender_access', label: 'Fire-tender access width (m)', type: 'number' },
  ]},
  { group: 'Water & environment', items: [
    { id: 'rwh', label: 'Rainwater harvesting provided', type: 'bool' },
    { id: 'stp', label: 'Sewage Treatment Plant provided', type: 'bool' },
    { id: 'rwst', label: 'Underground / RW storage tank', type: 'bool' },
    { id: 'percolation_well', label: 'Percolation well', type: 'bool' },
  ]},
];

/**
 * Land-use zones the Indian DCRs actually distinguish between.
 *
 * Deliberately short. A longer list invites a precision the app cannot use: no
 * param rule keys on zone yet, so today this improves what the assistant
 * retrieves and answers rather than changing a computed verdict.
 */
const LAND_USE_ZONES = [
  { v: 'residential',   t: 'Residential' },
  { v: 'commercial',    t: 'Commercial' },
  { v: 'industrial',    t: 'Industrial' },
  { v: 'mixed',         t: 'Mixed use' },
  { v: 'institutional', t: 'Institutional / public' },
  { v: 'recreational',  t: 'Recreational / green' },
  { v: 'agricultural',  t: 'Agricultural' },
];


function PaneLeft({
  savedProjects,
  setSavedProjects,
  setSelectedProject,
  setMainMode,
}) {
  const [activeProject, setActiveProject] = useState(null); // clicked project
  const [activeTab, setActiveTab] = useState('detail'); // 'detail' | 'view' | 'analysis' | 'build'
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); 
  const [isDesignActive, setIsDesignActive] = useState(false);
  const [massingLocked, setMassingLocked] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');

  const [edgeSelected, setEdgeSelected] = useState(false);
  const [massingActive, setMassingActive] = useState(false);
  // The massing can EXIST but be hidden, because the traced model took the
  // screen — the two cannot usefully share it. Without this the button would
  // read "Hide Massing" while no massing is visible, and clicking it would
  // destroy a massing the user cannot even see.
  // Either the traced plan OR an uploaded model can be holding the stage, so
  // track both — the button reads the same either way, but restoring the massing
  // has to switch off whichever one is actually showing.
  const [hiddenBy, setHiddenBy] = useState(null);   // 'trace' | 'model' | null
  useEffect(() => {
    const onTrace = (e) => setHiddenBy((h) => (e.detail?.visible ? 'trace' : (h === 'trace' ? null : h)));
    const onModel = (e) => setHiddenBy((h) => (e.detail?.visible ? 'model' : (h === 'model' ? null : h)));
    window.addEventListener('traceMassingVisibility', onTrace);
    window.addEventListener('designModelVisibility', onModel);
    return () => {
      window.removeEventListener('traceMassingVisibility', onTrace);
      window.removeEventListener('designModelVisibility', onModel);
    };
  }, []);
  const massingHiddenByTrace = hiddenBy !== null;
  const [exactCalc, setExactCalc] = useState(null);
  const [floorHeights, setFloorHeights] = useState([]);
  const [heightAlert, setHeightAlert] = useState('');
  // Selected road edges, each with its OWN width: a plot can abut several roads
  // of different widths, and the DCR setback band is read per abutting road.
  const [roadEdges, setRoadEdges] = useState([]);          // [{index, name, widthM, detected}]
  // Amenity counts/presence — pure inputs, nothing in the massing produces them.
  const [amenities, setAmenities] = useState({});
  const [showAmenities, setShowAmenities] = useState(false);

  const [siteInputs, setSiteInputs] = useState({
    setback: '', roadMargin: '', heightLimit: '', fsi: '', zone: '',
  });

  useEffect(() => {
    const onEdge = () => setEdgeSelected(true);
    window.addEventListener('frontEdgeSelected', onEdge);
    return () => window.removeEventListener('frontEdgeSelected', onEdge);
  }, []);

  useEffect(() => {
    const onMassing = (e) => { setExactCalc(e.detail); setMassingActive(true); };   // {floors, footprintAreaM2, builtUpAreaSqft}
    window.addEventListener('massingBuilt', onMassing);
    return () => window.removeEventListener('massingBuilt', onMassing);
  }, []);

  useEffect(() => { setExactCalc(null); }, [siteInputs]);

  // New project selected → exit Design Mode and reset all massing parameters
  useEffect(() => {
    setIsDesignActive(false);
    setMassingActive(false);
    setMassingLocked(false);
    setExactCalc(null);
    setFloorHeights([]);
    setHeightAlert('');
    setEdgeSelected(false);
    // Seeded from what was saved, not blanked. Switching away and back used to
    // clear these fields on screen while siteData still held the values, so the
    // panel disagreed with the project it was showing.
    const sd = activeProject?.siteData || {};
    setSiteInputs({
      setback: sd.siteSetback ?? '', roadMargin: sd.roadMargin ?? '',
      heightLimit: sd.heightLimit ?? '', fsi: sd.fsi ?? '', zone: sd.zone ?? '',
    });
    setRoadEdges([]);
    setAmenities(activeProject?.amenities || {});
  }, [activeProject?.id]);

  // Signing out has to close the open project, not just empty the list.
  //
  // activeProject is a full COPY of the project held here, so clearing
  // savedProjects up in App leaves this panel still rendering the plot area,
  // coordinates and drawings of whoever was signed in a moment ago —
  // showingList is derived from activeProject, not from the list.
  //
  // Setting it to null falls through to the reset above, which drops the
  // massing and design state that belongs to that project too.
  useEffect(() => {
    const onAuth = (e) => {
      if (e.detail?.signedIn) return;
      setActiveProject(null);
      setIsDropdownOpen(false);
      setProjectSearch('');
    };
    window.addEventListener('decon:auth', onAuth);
    return () => window.removeEventListener('decon:auth', onAuth);
  }, []);

  // Push per-edge widths + amenities onto the project so Site3DView can emit
  // them as compliance facts / achieved values.
  useEffect(() => {
    if (!activeProject) return;
    const widths = {};
    roadEdges.forEach((r) => {
      const v = parseFloat(r.widthM);
      if (Number.isFinite(v) && v > 0) widths[r.index] = v;
    });
    const margins = {};
    roadEdges.forEach((r) => {
      const v = parseFloat(r.marginM);
      if (Number.isFinite(v) && v >= 0) margins[r.index] = v;
    });
    activeProject.roadEdgeWidths = widths;
    activeProject.roadEdgeMargins = margins;
    activeProject.roadEdgeNames = Object.fromEntries(roadEdges.map((r) => [r.index, r.name]));

    // The legacy single roadMargin still feeds builtUpCalc and the massing
    // fallback — keep it as the STRICTEST abutting margin so the preview never
    // under-states the setback.
    const marginVals = Object.values(margins);
    if (marginVals.length) {
      const strictest = String(Math.max(...marginVals));
      if (strictest !== siteInputs.roadMargin) {
        setSiteInputs((si) => ({ ...si, roadMargin: strictest }));
      }
    }
    // governing road width = widest abutting road, unless typed explicitly
    const typed = parseFloat(siteInputs.roadWidth);
    const widest = Object.values(widths).length ? Math.max(...Object.values(widths)) : null;
    activeProject.roadWidthM = Number.isFinite(typed) && typed > 0 ? typed : widest;
    activeProject.amenities = amenities;
  }, [roadEdges, amenities, siteInputs.roadWidth, activeProject]);

  useEffect(() => {
    const onRoadEdges = (e) => {
      const d = e.detail || {};
      setEdgeSelected((d.count || 0) > 0);
      const detected = d.detected?.edges || [];
      // keep SELECTION order (the event carries the Set's insertion order) so
      // the row number always matches the badge drawn in the 3D view
      setRoadEdges((prev) => (d.indices || []).map((idx, n) => {
        const kept = prev.find((r) => r.index === idx);
        const hit = detected.find((x) => x.index === idx);
        // reopening a project: bring back the widths/margins already entered
        const savedW = activeProject?.roadEdgeWidths?.[idx];
        const savedM = activeProject?.roadEdgeMargins?.[idx];
        return kept || {
          index: idx,
          name: activeProject?.roadEdgeNames?.[idx] || hit?.road?.name || `Road ${n + 1}`,
          // OSM candidate pre-fills the NAME only — width stays blank until the
          // architect enters or accepts it. It drives a demolition-severity setback.
          marginM: savedM != null ? String(savedM) : '',
          widthM: savedW != null ? String(savedW) : '',
          detected: hit?.road || null,
        };
      }));
    };
    window.addEventListener('roadEdgesChanged', onRoadEdges);
    return () => window.removeEventListener('roadEdgesChanged', onRoadEdges);
  }, []);

  /**
   * Persist the site parameters whether or not a massing exists.
   *
   * The effect below only writes them once massingActive is true, because its real
   * job is triggering a rebuild. That was fine while these values only mattered to
   * the massing — but the zone matters to the assistant and to a compliance
   * lookup BEFORE anything is built, which is exactly when someone asks what
   * setback applies. Written straight through; no rebuild is dispatched here.
   */
  useEffect(() => {
    if (!activeProject) return;
    activeProject.siteData = {
      ...(activeProject.siteData || {}),
      siteSetback: siteInputs.setback,
      roadMargin:  siteInputs.roadMargin,
      heightLimit: siteInputs.heightLimit,
      fsi:         siteInputs.fsi,
      zone:        siteInputs.zone,
    };
    // The land-use zone is declared by the user, not read off a GIS layer.
    // ComplianceCard shows a warning for anything that is not 'gis' or
    // 'confirmed', which is the honest state for a typed value.
    activeProject.zoneConfidence = siteInputs.zone ? 'user' : null;
  }, [siteInputs, activeProject]);


  useEffect(() => {
    if (!massingActive || !activeProject) return;

    // write the new params IMMEDIATELY so any rebuild reads fresh values
    activeProject.siteData = {
      ...(activeProject.siteData || {}),
      siteSetback: siteInputs.setback,
      roadMargin:  siteInputs.roadMargin,
      heightLimit: siteInputs.heightLimit,
      fsi:         siteInputs.fsi,
      zone:        siteInputs.zone,
    };

    // debounce only the rebuild dispatch (avoids spamming rebuilds while typing)
    const t = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('build3DRequested'));
    }, 350);
    return () => clearTimeout(t);
    // roadEdges MUST be a dependency: per-edge margins change the footprint on
    // their own edge, and editing a non-strictest edge leaves siteInputs
    // untouched (roadMargin tracks the max), so without this the 2nd, 3rd...
    // road edge would silently never rebuild the massing.
  }, [siteInputs, roadEdges, massingActive, massingLocked]);

  useEffect(() => {
    if (!isDesignActive) setEdgeSelected(false);
  }, [isDesignActive]);

  // Site3DView tears the massing down when Design Mode is switched off; keep the
  // Build Up / Hide Massing button in step so it can't claim a massing exists.
  useEffect(() => {
    const onCleared = () => { setMassingActive(false); setExactCalc(null); };
    window.addEventListener('massingCleared', onCleared);
    return () => window.removeEventListener('massingCleared', onCleared);
  }, []);

  // resize floor-height array when floor count changes
  useEffect(() => {
    const n = exactCalc?.floors || 0;
    if (n === 0) return;
    setFloorHeights((prev) => {
      const next = [...prev];
      while (next.length < n) next.push(3);
      next.length = n;
      return next;
    });
  }, [exactCalc?.floors]);

  // over-limit alert from the massing
  useEffect(() => {
    const onStatus = (e) => {
      const over = e.detail?.overLimitCount || 0;
      setHeightAlert(over > 0
        ? `Height limit exceeded — ${over} floor plate(s) beyond the ${e.detail.shellHeight} m envelope are hidden.`
        : '');
    };
    window.addEventListener('floorHeightStatus', onStatus);
    return () => window.removeEventListener('floorHeightStatus', onStatus);
  }, []);

  useEffect(() => {
    setFloorHeights((prev) => {
      const reset = prev.map(() => 3);
      window.dispatchEvent(new CustomEvent('setFloorHeights', { detail: { heights: reset } }));
      return reset;
    });
  }, [siteInputs.setback, siteInputs.roadMargin, siteInputs.heightLimit, siteInputs.fsi]);

  useEffect(() => {
    // reset floor heights to default when the core massing params change
    setFloorHeights((prev) => prev.map(() => 3));
  }, [siteInputs.setback, siteInputs.roadMargin, siteInputs.heightLimit, siteInputs.fsi]);

  const updateFloorHeight = (i, val) => {
    const next = [...floorHeights];
    next[i] = parseFloat(val) || 0;
    setFloorHeights(next);
    window.dispatchEvent(new CustomEvent('setFloorHeights', { detail: { heights: next } }));
  };

  // ---- derived feasibility numbers (approximate; massing computes exact) ----
  const FLOOR_H = 3;
  const bfNums = {
    setback: parseFloat(siteInputs.setback),
    roadMargin: parseFloat(siteInputs.roadMargin),
    heightLimit: parseFloat(siteInputs.heightLimit),
    fsi: parseFloat(siteInputs.fsi),
  };
  const bfAllFilled = Object.values(bfNums).every((v) => Number.isFinite(v) && v >= 0);

  let bfCalc = null;
  if (bfAllFilled && activeProject?.areaM2) {
    const gross = activeProject.areaM2;
    const perim = activeProject.perimeterM || 4 * Math.sqrt(gross);
    let footprint = gross - perim * bfNums.setback + 4 * bfNums.setback * bfNums.setback;
    footprint = Math.max(0, Math.min(footprint, gross));
    const byH = Math.floor(bfNums.heightLimit / FLOOR_H);
    const byF = footprint > 0 ? Math.floor((bfNums.fsi * gross) / footprint) : 0;
    const floors = Math.max(0, Math.min(byH, byF));
    bfCalc = {
      groundCoverage: footprint.toFixed(1),
      builtUp: Math.min(footprint * floors, bfNums.fsi * gross).toFixed(1),
      floors,
      limitedBy: byH <= byF ? 'height' : 'FSI',
    };
  }

  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString();
  };


   useEffect(() => {
    const onEdge = () => setEdgeSelected(true);
    window.addEventListener('frontEdgeSelected', onEdge);
    return () => window.removeEventListener('frontEdgeSelected', onEdge);
  }, []);

  // reset the flow when design mode turns off
  useEffect(() => {
    if (!isDesignActive) setEdgeSelected(false);
  }, [isDesignActive]);

  // Site3DView tears the massing down when Design Mode is switched off; keep the
  // Build Up / Hide Massing button in step so it can't claim a massing exists.
  useEffect(() => {
    const onCleared = () => { setMassingActive(false); setExactCalc(null); };
    window.addEventListener('massingCleared', onCleared);
    return () => window.removeEventListener('massingCleared', onCleared);
  }, []);

  useEffect(() => {
    const onDesignChange = (e) => setIsDesignActive(!!e.detail?.active);
    window.addEventListener('designModeChanged', onDesignChange);
    return () => window.removeEventListener('designModeChanged', onDesignChange);
  }, []);

  const normalizeSigned = (deg) => ((deg + 180) % 360) - 180;

  useEffect(() => {
    const handleActivateBuild = () => {
      setActiveTab('build');
    };

    window.addEventListener('activateBuildTab', handleActivateBuild);
    return () => window.removeEventListener('activateBuildTab', handleActivateBuild);
  }, []);

  useEffect(() => {
    if (!activeProject) return;
    // 1. hand the new project up to the parent → Three.js window reads it as a prop
    setSelectedProject(activeProject);
    // 2. tell the 3D/map layer to rebuild the site (matches your existing event pattern)
    window.dispatchEvent(
      new CustomEvent('loadSiteRequested', { detail: { project: activeProject } })
    );
  }, [activeProject]);

  useEffect(() => {
    if (!activeProject) return;

    const geoReady =
      Array.isArray(activeProject.polygonLatLng) &&
      activeProject.polygonLatLng.length >= 3 &&
      activeProject.centerLat != null &&
      activeProject.centerLng != null;

    setSelectedProject(activeProject);   // updates the prop Site3DView reads

    if (geoReady) {
      window.dispatchEvent(new CustomEvent('loadSiteRequested', { detail: { project: activeProject } }));
    } else {
      console.warn('Project has no geo polygon — 3D site will not build:', activeProject?.name);
    }
  }, [activeProject]);

  // Open a project on request from outside this pane. The drawing intake on the
  // main pane sends a file to a project, then asks for that project to be opened
  // in 3D. The event carries the server's saved copy — the one with the drawing
  // just attached — because this pane's own list has not re-rendered with it yet.
  // Going through setActiveProject keeps this pane, the selected project and the
  // 3D site in step, exactly as a click in the project list does.
  // A project just created from the main pane, until its owner opens it: its View
  // button glows, so the next step is plain rather than something to look for.
  const [freshProjectId, setFreshProjectId] = useState(null);
  const savedProjectsRef = useRef(savedProjects);
  useEffect(() => { savedProjectsRef.current = savedProjects; }, [savedProjects]);
  useEffect(() => {
    const onOpen = (e) => {
      const d = (e && e.detail) || {};
      const p = d.project
        || (savedProjectsRef.current || []).find((x) => String(x.id) === String(d.projectId));
      if (!p) return;
      setActiveProject(p);
      setActiveTab('detail');
      setIsDropdownOpen(false);
      // `view: false` selects the project without opening 3D — the drawing flow
      // leaves the new project selected and lets its owner press View.
      if (d.view !== false) { setFreshProjectId(null); setMainMode('3d'); } else setFreshProjectId(p.id);
    };
    window.addEventListener('openProjectRequested', onOpen);
    return () => window.removeEventListener('openProjectRequested', onOpen);
  }, [setMainMode]);

  // The project list closes on a press anywhere outside it and its toggle, and on
  // Escape — which also hands focus back to the toggle the keyboard came from.
  // Capture phase, so a surface that stops its own pointer events (the map, the
  // 3D canvas) still closes it.
  const dropdownRef = useRef(null);
  const dropdownToggleRef = useRef(null);
  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    const close = () => { setIsDropdownOpen(false); setProjectSearch(''); };
    const onDown = (e) => {
      if (dropdownRef.current?.contains(e.target) || dropdownToggleRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      close();
      dropdownToggleRef.current?.focus();
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [isDropdownOpen]);

  // Selecting any other project ends the prompt — it is for the one just made.
  useEffect(() => {
    if (freshProjectId != null && String(activeProject?.id) !== String(freshProjectId)) setFreshProjectId(null);
  }, [activeProject?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen3D = () => {
    if (!activeProject) return;
    setSelectedProject(activeProject);
    setFreshProjectId(null);
    setMainMode('3d');
    // NO setActiveTab — Detail stays default
  };

  // Delete is now a small icon, so it must not fire on one press.
  //
  // The first press ARMS it — the button widens and says "Delete?" — and only a
  // second press within a few seconds actually deletes. It disarms itself after
  // that, so a stray click never leaves a loaded trigger sitting there. This is
  // a browser confirm() dialog's job, but a dialog stops everything and gets
  // dismissed reflexively; arming keeps the decision on the control itself.
  //
  // The project and its drawings are removed on the server with no undo, which
  // is the whole reason a one-click icon would have been the wrong build.
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  useEffect(() => {
    if (!deleteArmed) return undefined;
    const t = setTimeout(() => setDeleteArmed(false), 4000);
    return () => clearTimeout(t);
  }, [deleteArmed]);
  useEffect(() => { setDeleteArmed(false); setDeleteError(null); }, [activeProject?.id]);

  const handleDeleteActive = async () => {
    if (!deleteArmed) { setDeleteArmed(true); setDeleteError(null); return; }
    setDeleteArmed(false);
    console.log('Delete clicked, activeProject =', activeProject);
    if (!activeProject) return;

    const projectId = activeProject.id;
    if (projectId == null) {
      console.error('Active project has no id; cannot delete on server');
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/projects/${projectId}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        console.error('Delete failed body:', text);
        throw new Error('Failed to delete project on server');
      }

      setSavedProjects((prev) => prev.filter((p) => p.id !== projectId));
      setActiveProject(null);
      setIsDropdownOpen(false);
    } catch (err) {
      console.error('Failed to delete project', err);
    }
  };

  const handleClickProjectName = (project) => {
    console.log('Clicked project in list:', project);
    setActiveProject(project);
    setActiveTab('detail');
    setIsDropdownOpen(false);
  };

  const handleSelectFromDropdown = (project) => {
    setActiveProject(project);
    setActiveTab('detail');
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    if (!activeProject) return;
    setIsDropdownOpen((prev) => !prev);
  };

  const showingList = !activeProject; // initial list vs active-project view

  return (
    <aside className="pane-left">
      <div className="pane-left-inner">
        {/* header */}
        <div className="pane-left-header">
          {showingList ? (
            <>
              <div className="pane-left-title">Model Tree</div>
              <div className="small muted">
                {savedProjects.length > 0 ? `${savedProjects.length} project(s)` : 'No projects'}
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="pane-left-title" style={{ flex: 1 }}>{activeProject?.name}</div>
                <button
                  ref={dropdownToggleRef}
                  type="button"
                  className={`dropdown-toggle-btn ${isDropdownOpen ? 'is-open' : ''}`}
                  onClick={toggleDropdown}
                  aria-expanded={isDropdownOpen}
                  aria-controls="project-dropdown"
                  aria-label={isDropdownOpen ? 'Close the project list' : 'Switch project'}
                  title={isDropdownOpen ? 'Close the project list — or click anywhere else' : 'Switch project'}
                >
                  ▾
                </button>
              </div>
              <div className="small muted">{formatDateTime(activeProject?.createdAt)}</div>
              {/* The project dropdown is drawn once, under .node--active-project below,
                  whose position: relative anchors it in this pane. A second copy here
                  had no positioned ancestor, so it was placed against the page and
                  showed up again, full width, under the footer. */}
            </>
          )}
        </div>
        {/* Pinned. These were inside .pane-left-content, so the tab row scrolled
            away with the panel it controls — you could be reading Build inputs
            with no way to see which tab you were on, or to leave it, without
            scrolling back up. Now only what is UNDER the tabs moves. */}
        {!showingList && activeProject && (
          <div className="project-tabs">
            <button
              type="button"
              className={
                'btn small project-tab-btn' +
                (activeTab === 'detail'
                  ? ' project-tab-btn--active'
                  : '')
              }
              onClick={() => setActiveTab('detail')}
            >
              Detail
            </button>
            <button
              type="button"
              className={
                'btn small project-tab-btn' +
                (activeTab === 'build'
                  ? ' project-tab-btn--active'
                  : '')
              }
              onClick={() => setActiveTab('build')}
            >
              Build
            </button>
            <button
              type="button"
              className={
                'btn small project-tab-btn' +
                (activeTab === 'status'
                  ? ' project-tab-btn--active'
                  : '')
              }
              onClick={() => setActiveTab('status')}
            >
              Status
            </button>
          </div>
        )}


        {/* scrollable content: list + details */}
        <div className="pane-left-content">
          {/* project area */}
          <div className="project-tree">
            {showingList &&
              savedProjects.map((project, idx) => (
                <div
                  key={project.id ?? project.createdAt ?? idx}
                  className="node node--project-list"
                >
                  <span
                    className="project-name"
                    onClick={() => handleClickProjectName(project)}
                  >
                    {project.name}
                    <div className="small muted">
                      {formatDateTime(project.createdAt)}
                    </div>
                  </span>
                </div>
              ))}

            {!showingList && activeProject && (
              <div className="node node--active-project">

                {/* dropdown with all saved projects */}
                {isDropdownOpen && (
                  <div className="project-dropdown" id="project-dropdown" ref={dropdownRef}>
                    <input
                      type="text"
                      placeholder="Search projects…"
                      value={projectSearch}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setProjectSearch(e.target.value)}
                      className="project-dropdown-search"
                    />
                    <div className="project-dropdown-list">
                      {savedProjects
                        .filter((p) => p.name.toLowerCase().includes(projectSearch.toLowerCase()))
                        .map((p, i) => (
                          <div
                            key={p.id ?? p.createdAt ?? i}
                            className={
                              'project-dropdown-item' +
                              (activeProject === p ? ' project-dropdown-item--active' : '')
                            }
                            onClick={() => { handleSelectFromDropdown(p); setProjectSearch(''); }}
                          >
                            {p.name}
                          </div>
                        ))}
                      {projectSearch && !savedProjects.some((p) => p.name.toLowerCase().includes(projectSearch.toLowerCase())) && (
                        <div className="project-dropdown-empty">No projects match &ldquo;{projectSearch}&rdquo;</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Detail / View / Site Analysis / Build buttons under name */}
              </div>
            )}
          </div>

          {/* details area driven by activeTab */}
          {activeProject && (
            <div className="panel project-details-panel">
              {activeTab === 'detail' && (
                <>
                  <div>Area: {activeProject.areaM2.toFixed(1)} m²</div>
                  <div>Perimeter: {activeProject.perimeterM.toFixed(1)} m</div>
                  <div>Area (sqft): {activeProject.areaSqft.toFixed(1)}</div>
                  <div>Area (acre): {activeProject.areaAcre.toFixed(4)}</div>
                  <div>
                    Angle from origin edge to north:{' '}
                    {activeProject.originEdgeBearingDeg != null
                      ? `${normalizeSigned(
                          activeProject.originEdgeBearingDeg
                        ).toFixed(1)}°`
                      : '—'}
                  </div>
                  <div>
                    Site Latitude:{' '}
                    {activeProject.centerLat != null
                      ? activeProject.centerLat.toFixed(6)
                      : '—'}
                  </div>
                  <div>
                    Site Longitude:{' '}
                    {activeProject.centerLng != null
                      ? activeProject.centerLng.toFixed(6)
                      : '—'}
                  </div>
                  <div>
                    Location:{' '}
                    {activeProject.location &&
                    activeProject.location.trim()
                      ? activeProject.location
                      : '—'}
                  </div>
                </>
              )}

              {activeTab === 'view' && <></>}

              {activeTab === 'analysis' && (
                <>
                  <div className="small muted" style={{ marginTop: 4 }}>
                    Polygon vertices (local, bottom-left = 0,0):
                  </div>
                  <div className="small project-vertices-list">
                    {activeProject.polygonLocal.map((pt, index) => (
                      <div key={index}>
                        {index + 1}: ({pt.x.toFixed(2)}, {pt.y.toFixed(2)})
                      </div>
                    ))}
                  </div>
                  <div>
                    Development Zone: {activeProject.developmentZone || '—'}
                  </div>
                  <div>Climate: {activeProject.climate || '—'}</div>
                  <div>Flood Risk: {activeProject.floodRisk || '—'}</div>
                  <div>
                    Earthquake Risk: {activeProject.earthquakeRisk || '—'}
                  </div>
                  <div>Water Access: {activeProject.waterAccess || '—'}</div>
                  <div>Air Pollution: {activeProject.airPollution || '—'}</div>
                  <div>Water Pollution: {activeProject.waterPollution || '—'}</div>
                  <div>Noise Pollution: {activeProject.noisePollution || '—'}</div>
                  <div>Soil Type: {activeProject.soilType || '—'}</div>

                  <div className="small muted" style={{ marginTop: 4 }}>
                    Nearby facilities:
                  </div>
                  <div className="small project-vertices-list">
                    <div>
                      Bus Stand: {activeProject.facilities?.busStand || '—'}
                    </div>
                    <div>
                      Metro Station: {activeProject.facilities?.metro || '—'}
                    </div>
                    <div>
                      Railway Station: {activeProject.facilities?.railway || '—'}
                    </div>
                    <div>
                      Hospital: {activeProject.facilities?.hospital || '—'}
                    </div>
                    <div>Mall: {activeProject.facilities?.mall || '—'}</div>
                    <div>Park/Garden: {activeProject.facilities?.park || '—'}</div>
                    <div>
                      Open Ground: {activeProject.facilities?.openGround || '—'}
                    </div>
                    <div>
                      Multi‑Purpose Hall: {activeProject.facilities?.hall || '—'}
                    </div>
                    <div>Lake: {activeProject.facilities?.lake || '—'}</div>
                    <div>River: {activeProject.facilities?.river || '—'}</div>
                  </div>

                  <div>
                    Height from Sea Level: {activeProject.elevationM ?? '—'} m
                  </div>
                  <div>
                    Culture Activities: {activeProject.cultureActivities || '—'}
                  </div>
                  <div>Vaastu Tips: {activeProject.vaastuTips || '—'}</div>
                </>
              )}

              {activeTab === 'build' && (
                <>
                  {isDesignActive && activeProject && (
                    <div className="dm-panel">
                      <button
                        type="button"
                        className={`btn dm-edge-btn ${edgeSelected ? 'btn--edge-ok' : 'primary'}`}
                        disabled={massingLocked}
                        onClick={() => window.dispatchEvent(new CustomEvent('startFrontEdgePickRequested'))}
                      >
                        {edgeSelected
                          ? `✓ ${activeProject?.roadEdgeIndices?.length || 1} road edge(s) — tap to edit`
                          : 'Select site edge(s) towards road'}
                      </button>

                      {!edgeSelected && (
                        <p className="dm-hint">Select the road-facing edge in the 3D view to unlock the inputs below.</p>
                      )}

                      {roadEdges.length > 0 && (
                        <div className="dm-edge-list">
                          <div className="dm-edge-head">
                            <span className="dm-edge-head-name">Edge</span>
                            <span className="dm-edge-head-num">Width m</span>
                            <span className="dm-edge-head-num">Margin m</span>
                          </div>
                          {roadEdges.map((r, i) => (
                            <div className="dm-edge-row" key={r.index}>
                              <span
                                className="dm-edge-badge"
                                title={`Road edge ${i + 1} — marked ${i + 1} in the 3D view`}
                              >
                                {i + 1}
                              </span>
                              <input
                                className="dm-edge-width"
                                type="number" min="0" step="0.5"
                                placeholder="width"
                                value={r.widthM}
                                title="Width of THIS road (m) — drives which setback band applies"
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setRoadEdges((list) => list.map((x) => x.index === r.index ? { ...x, widthM: v } : x));
                                }}
                              />
                              <input
                                className="dm-edge-width"
                                type="number" min="0" step="0.5"
                                placeholder="margin"
                                value={r.marginM || ''}
                                disabled={massingLocked}
                                title="Required margin from THIS road (m) — sets the footprint on this edge"
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setRoadEdges((list) => list.map((x) => x.index === r.index ? { ...x, marginM: v } : x));
                                }}
                              />
                              {r.detected?.widthM != null && !r.widthM && (
                                <button
                                  type="button"
                                  className="dm-edge-suggest"
                                  title={`${r.detected.basis} — carriageway only, verify`}
                                  onClick={() => setRoadEdges((list) => list.map((x) =>
                                    x.index === r.index ? { ...x, widthM: String(r.detected.widthM) } : x))}
                                >
                                  {r.detected.widthM}?
                                </button>
                              )}
                            </div>
                          ))}
                          <p className="dm-hint dm-hint--tight">
                            Numbers match the badges in the 3D view.
                            Widths marked “?” are OSM estimates — confirm before relying on them.
                          </p>
                        </div>
                      )}

                      {activeProject?.areaM2 && (
                        <div className="dm-area">
                          <span className="dm-area-label">Site Area - </span>
                          <span>
                            <span className="dm-area-value">{activeProject.areaM2.toFixed(1)} m²/ </span>
                            <span className="dm-area-sub">
                              {Math.round(activeProject.areaM2 * 10.7639).toLocaleString()} sqft
                            </span>
                          </span>
                        </div>
                      )}

                      <div className="dm-section-title">Site Parameters</div>

                      <div className="dm-field">
                        <label>Site Setback (m)</label>
                        <input className="dm-input" type="number" min="0" value={siteInputs.setback}
                          disabled={massingLocked}
                          onChange={(e) => setSiteInputs({ ...siteInputs, setback: e.target.value })} />
                      </div>


                      {/* Land use zone. Read by the compliance resolver and by the assistant,
                          and until now there was no way to supply it at all: project.zone was
                          read in three places and written in none, so any answer that depended
                          on it dead-ended at "NOT CAPTURED YET". */}
                      <div className="dm-field">
                        <label>Land use zone</label>
                        <select className="dm-input" value={siteInputs.zone}
                          disabled={massingLocked}
                          onChange={(e) => setSiteInputs({ ...siteInputs, zone: e.target.value })}>
                          <option value="">Not set</option>
                          {LAND_USE_ZONES.map((z) => (
                            <option key={z.v} value={z.v}>{z.t}</option>
                          ))}
                        </select>
                      </div>

                      <div className="dm-row">
                        <div className="dm-field">
                          <label>Height Limit (m)</label>
                          <input className="dm-input" type="number" min="0" value={siteInputs.heightLimit}
                            disabled={massingLocked}
                            onChange={(e) => setSiteInputs({ ...siteInputs, heightLimit: e.target.value })} />
                        </div>
                        <div className="dm-field">
                          <label>FSI</label>
                          <input className="dm-input" type="number" min="0" step="0.1" value={siteInputs.fsi}
                            disabled={massingLocked}
                            onChange={(e) => setSiteInputs({ ...siteInputs, fsi: e.target.value })} />
                        </div>
                      </div>

                      {/* Amenities & services — pure inputs. Nothing in the
                          massing produces these, and two of them (staircases,
                          fire access) are stop-work severity. */}
                      <button
                        type="button"
                        className="dm-amenity-toggle"
                        onClick={() => setShowAmenities((v) => !v)}
                      >
                        {showAmenities ? '▾' : '▸'} Amenities &amp; services
                        <span className="dm-amenity-count">
                          {Object.values(amenities).filter((v) => v !== '' && v != null && v !== false).length} set
                        </span>
                      </button>

                      {showAmenities && (
                        <div className="dm-amenity-body">
                          {AMENITY_FIELDS.map((sec) => (
                            <div key={sec.group} className="dm-amenity-group">
                              <div className="dm-amenity-group-title">{sec.group}</div>
                              {sec.items.map((f) => (
                                <div className="dm-field dm-amenity-field" key={f.id}>
                                  <label>{f.label}</label>
                                  {f.type === 'bool' ? (
                                    <input
                                      type="checkbox"
                                      className="dm-amenity-check"
                                      checked={!!amenities[f.id]}
                                      onChange={(e) => setAmenities((a) => ({ ...a, [f.id]: e.target.checked }))}
                                    />
                                  ) : (
                                    <input
                                      className="dm-input" type="number" min="0"
                                      value={amenities[f.id] ?? ''}
                                      onChange={(e) => setAmenities((a) => ({ ...a, [f.id]: e.target.value }))}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          ))}
                          <p className="dm-hint dm-hint--tight">
                            Entered values only — these are checked against the DCR once its
                            rules are encoded, and stay “needs input” until you fill them.
                          </p>
                        </div>
                      )}

                      {exactCalc && (
                        <div className="dm-stats">
                          <div className="dm-stat">
                            <div className="dm-stat-value">{exactCalc.footprintAreaM2.toFixed(0)}</div>
                            <div className="dm-stat-label">Ground m²</div>
                          </div>
                          <div className="dm-stat">
                            <div className="dm-stat-value">{(exactCalc.footprintAreaM2 * exactCalc.floors).toFixed(0)}</div>
                            <div className="dm-stat-label">Built-up m²</div>
                          </div>
                          <div className="dm-stat">
                            <div className="dm-stat-value">{exactCalc.floors}</div>
                            <div className="dm-stat-label">Floors</div>
                          </div>
                        </div>
                      )}

                      {exactCalc?.floors > 0 && (
                        <div>
                          <div className="dm-floors-title">Floor Heights (m)</div>
                          {floorHeights.map((h, i) => {
                            const cumulative = floorHeights.slice(0, i + 1).reduce((a, b) => a + b, 0);
                            const limit = parseFloat(siteInputs.heightLimit) || 3;
                            const over = cumulative > limit + 0.001;
                            const name = i === 0 ? 'Ground' : `${i}${i===1?'st':i===2?'nd':i===3?'rd':'th'} Floor`;
                            return (
                              <div key={i} className="dm-floor-row">
                                <span className={`dm-floor-name ${over ? 'over' : ''}`}>
                                  {name}{over ? ' · over limit' : ''}
                                </span>
                                <input className="dm-floor-input" type="number" min="2" step="0.1" value={h}
                                  disabled={massingLocked}
                                  onChange={(e) => updateFloorHeight(i, e.target.value)} />
                              </div>
                            );
                          })}
                          {heightAlert && <div className="dm-alert">⚠ {heightAlert}</div>}
                        </div>
                      )}
                    </div>
                  )}

                  {!isDesignActive && (
                    <div
                      className="design-mode-prompt"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8,
                        padding: '40px 24px',
                        textAlign: 'center',
                        color: 'var(--text-secondary, #9aa4b2)',
                        fontSize: 14,
                        lineHeight: 1.5,
                      }}
                    >
                      <div style={{ fontSize: 26, opacity: 0.7 }}>🏗️</div>
                      <div>
                        Turn on <strong style={{ color: 'var(--text, #e5e9f0)' }}>Design Mode</strong> to start building
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.75 }}>
                        Click the <strong>Design</strong> button in the 3D view.
                      </div>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'status' && (
                <ProjectStatusPanel
                  project={activeProject}
                  massing={exactCalc}
                  onUpdate={(updated) => {
                    setActiveProject(updated);
                    setSavedProjects((prev) => prev.map((p) => p.id === updated.id ? updated : p));
                    setSelectedProject(updated);
                  }}
                />
              )}

            </div>
          )}
        </div>
      </div>

      {/* fixed View / Delete at bottom; only when a project is active */}
      {activeProject && (
      <div className="project-bottom-actions">
        {deleteError && <div className="delete-error">{deleteError}</div>}
          {isDesignActive && activeTab === 'status' ? (
            <div style={{ display: 'flex', gap: 6, width: '100%' }}>
              <button type="button"
                className="btn build-up-btn is-ready"
                style={{ flex: 1 }}
                title="Open the cost sheet below the 3D view"
                onClick={() => window.dispatchEvent(new CustomEvent('toggleSheetRequested'))}>
                Sheet
              </button>
              <button type="button"
                onClick={() => setMassingLocked((v) => !v)}
                title={massingLocked ? 'Unlock' : 'Lock'}
                style={{ width: 42, borderRadius: 8, cursor: 'pointer',
                  border: '0.5px solid var(--border,#2c3444)',
                  background: massingLocked ? '#eab308' : 'transparent',
                  color: massingLocked ? '#111' : 'var(--text-secondary,#c3c9d4)', fontSize: 16 }}>
                {massingLocked ? '🔒' : '🔓'}
              </button>
            </div>
          ) : isDesignActive ? (
            <div style={{ display: 'flex', gap: 6, width: '100%' }}>
              <button type="button"
                className={`btn build-up-btn ${massingActive ? 'is-active' : 'is-ready'}`}
                style={{ flex: 1 }}
                title={massingHiddenByTrace
                  ? `The ${hiddenBy === 'model' ? 'uploaded' : 'traced'} model is showing — bring the massing back`
                  : 'Generate massing with floor slabs'}
                onClick={() => {
                  if (massingHiddenByTrace) {
                    // Switch off whichever subject is holding the stage...
                    window.dispatchEvent(new CustomEvent(
                      hiddenBy === 'model' ? 'toggleDesignModelRequested' : 'toggleTraceMassing',
                      { detail: { visible: false } },
                    ));
                    // ...and build a massing if there is not one yet. The button says
                    // "Show Massing", so it has to end with a massing on screen —
                    // hiding the model and revealing an empty site is not that.
                    if (!massingActive) {
                      activeProject.siteData = {
                        ...(activeProject.siteData || {}),
                        siteSetback: siteInputs.setback,
                        roadMargin: siteInputs.roadMargin,
                        heightLimit: siteInputs.heightLimit,
                        fsi: siteInputs.fsi,
                      };
                      window.dispatchEvent(new CustomEvent('build3DRequested'));
                    }
                  } else if (massingActive) {
                    window.dispatchEvent(new CustomEvent('clearMassingRequested'));
                    setMassingActive(false);
                    setExactCalc(null);
                  } else {
                    activeProject.siteData = {
                      ...(activeProject.siteData || {}),
                      siteSetback: siteInputs.setback,
                      roadMargin: siteInputs.roadMargin,
                      heightLimit: siteInputs.heightLimit,
                      fsi: siteInputs.fsi,
                    };
                    window.dispatchEvent(new CustomEvent('build3DRequested'));
                  }
                }}>
                {massingHiddenByTrace
                  ? 'Show Massing'
                  : massingActive ? 'Hide Massing' : 'Build Up'}
              </button>
              <button type="button"
                onClick={() => setMassingLocked((v) => !v)}
                title={massingLocked ? 'Unlock parameters' : 'Lock massing & parameters'}
                style={{ width: 42, borderRadius: 8, cursor: 'pointer',
                  border: '0.5px solid var(--border,#2c3444)',
                  background: massingLocked ? '#eab308' : 'transparent',
                  color: massingLocked ? '#111' : 'var(--text-secondary,#c3c9d4)', fontSize: 16 }}>
                {massingLocked ? '🔒' : '🔓'}
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                className={`btn view-primary${freshProjectId != null && String(activeProject.id) === String(freshProjectId) ? ' is-fresh' : ''}`}
                onClick={handleOpen3D}
                title={freshProjectId != null && String(activeProject.id) === String(freshProjectId)
                  ? 'Your new project is ready — open it in 3D'
                  : 'View 3D'}
              >
                View
              </button>
              <button
                type="button"
                className={`btn btn--danger btn--icon ${deleteArmed ? 'is-armed' : ''}`}
                onClick={handleDeleteActive}
                onBlur={() => setDeleteArmed(false)}
                aria-label={deleteArmed ? 'Confirm delete project' : 'Delete project'}
                title={deleteArmed
                  ? 'Press again to delete this project permanently'
                  : 'Delete project'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 7h16" />
                  <path d="M10 4h4a1 1 0 0 1 1 1v2H9V5a1 1 0 0 1 1-1Z" />
                  <path d="M6 7v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7" />
                  <path d="M10 11v6M14 11v6" />
                </svg>
                {deleteArmed && <span className="btn-icon-label">Delete?</span>}
              </button>
            </>
          )}
        </div> 
      )}
    </aside>
  );
}

export default PaneLeft;
